import React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import PayrollSessionsAccordion from './PayrollSessionsAccordion';
import PayrollSummaryCard from './PayrollSummaryCard';

function MetricTile({ label, value, tone = 'default' }) {
  const styles = {
    default: {
      bg: 'rgba(15,23,42,0.04)',
      border: 'rgba(15,23,42,0.08)',
      text: 'text.primary',
    },
    success: {
      bg: 'rgba(34,197,94,0.08)',
      border: 'rgba(34,197,94,0.18)',
      text: 'success.main',
    },
    warning: {
      bg: 'rgba(245,158,11,0.08)',
      border: 'rgba(245,158,11,0.18)',
      text: 'warning.main',
    },
  };

  const palette = styles[tone] || styles.default;

  return (
    <MDBox
      sx={{
        p: 1,
        borderRadius: '16px',
        border: `1px solid ${palette.border}`,
        bgcolor: palette.bg,
      }}
    >
      <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {label}
      </MDTypography>
      <MDTypography variant="h6" fontWeight="bold" sx={{ color: palette.text, lineHeight: 1.1 }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

export default function PayrollSessionsSection({
  rows = [],
  includeMissedSessions = false,
  applyCardCommission = false,
  cardCommissionPercent = 0,
  payrollType = 'HOURLY',
  monthlySalary = 0,
  onToggleIncluded,
  onChangePayrollAmount,
  onExportPdf,
  loading = false,
  error = '',
  rawCount = 0,
}) {
  const attendedCount = rows.filter((row) => row.attendanceStatus === 'ATTENDED').length;
  const hiddenCount = Math.max(Number(rawCount || 0) - Number(rows.length || 0), 0);
  const pricingWarningCount = new Set(
    rows.filter((row) => row.pricingWarningText).map((row) => String(row.sessionId))
  ).size;

  return (
    <MDBox sx={{ display: 'grid', gap: 1.2 }}>
      <Card
        sx={{
          borderRadius: '22px',
          border: '1px solid rgba(15,23,42,0.08)',
          boxShadow: 'none',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(248,250,252,0.96), rgba(255,255,255,0.98))',
        }}
      >
        <MDBox sx={{ p: { xs: 1.2, md: 1.5 }, display: 'grid', gap: 1.1 }}>
          <MDBox
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">
                Maaş Hesaplama
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Seansları filtrele, ücretleri düzenle ve hakedişi sade bir akışla kontrol et.
              </MDTypography>
            </MDBox>

            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`${rows.length} görünür seans`}
                sx={{ borderRadius: '999px', fontWeight: 800 }}
              />
              {hiddenCount > 0 ? (
                <Chip
                  size="small"
                  label={`${hiddenCount} filtrelendi`}
                  sx={{ borderRadius: '999px', fontWeight: 800 }}
                />
              ) : null}
              {pricingWarningCount > 0 ? (
                <Chip
                  size="small"
                  color="warning"
                  label={`${pricingWarningCount} fiyat uyarısı`}
                  sx={{ borderRadius: '999px', fontWeight: 800 }}
                />
              ) : null}
            </MDBox>
          </MDBox>

          <Grid container spacing={1}>
            <Grid item xs={6} md={3}>
              <MetricTile label="Toplam Kayıt" value={rawCount} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricTile label="Listelenen" value={rows.length} tone="success" />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricTile label="Katılan" value={attendedCount} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricTile
                label="Katılım Olmayanlar"
                value={includeMissedSessions ? 'Açık' : 'Kapalı'}
                tone={includeMissedSessions ? 'warning' : 'default'}
              />
            </Grid>
            {payrollType === 'MONTHLY' ? (
              <Grid item xs={12} md={3}>
                <MetricTile
                  label="Aylık Maaş"
                  value={`${Number(monthlySalary || 0).toLocaleString('tr-TR')} ₺`}
                  tone="success"
                />
              </Grid>
            ) : null}
          </Grid>
        </MDBox>
      </Card>

      {loading ? (
        <Card
          sx={{
            borderRadius: '22px',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: 'none',
          }}
        >
          <MDBox py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
            <CircularProgress size={28} />
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Seanslar yükleniyor...
            </MDTypography>
          </MDBox>
        </Card>
      ) : null}

      {!!error && !loading ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error ? (
        <Grid container spacing={1.25}>
          <Grid item xs={12} xl={8.7}>
            <PayrollSessionsAccordion
              rows={rows}
              includeMissedSessions={includeMissedSessions}
              applyCardCommission={applyCardCommission}
              cardCommissionPercent={cardCommissionPercent}
              onToggleIncluded={onToggleIncluded}
              onChangePayrollAmount={onChangePayrollAmount}
              onExportPdf={onExportPdf}
            />
          </Grid>

          <Grid item xs={12} xl={3.3}>
            <PayrollSummaryCard
              rows={rows}
              applyCardCommission={applyCardCommission}
              cardCommissionPercent={cardCommissionPercent}
              payrollType={payrollType}
              monthlySalary={monthlySalary}
            />
          </Grid>
        </Grid>
      ) : null}
    </MDBox>
  );
}
